import { FormattedMessage } from "react-intl";
import { useEffect, useState } from "react";
import { Drawer } from "antd";
import { animated } from "@react-spring/web";
import { Button } from "@letsbit/milkylb";
import { Link } from "react-router-dom";
import classNames from "classnames";
import {
  HISTORY_OPERATION_TYPE,
  RECORD_STATE,
  UserHistoryResponse,
} from "@letsbit/malcolmlb";
import InfiniteScroll from "react-infinite-scroll-component";

import { ReactComponent as AdjustIcon } from "assets/images/icons/adjust.svg";
import { HisotryInfiniteScrollProps, HistoryFilter } from "./types";
import { messages } from "./messages";
import { Pill } from "components/Pill";
import {
  historyFiltersToParams,
  useHistoryInfiniteScroll,
} from "./useHistoryInfiniteScroll";
import CurrencyOperationCard from "components/CurrencyOperationCard";
import { HistoryAdvancedSearch } from "./HistoryAdvancedSearch";
import { HistoryDetails } from "./HistoryDetails";
import { MiniLoading } from "components/MiniLoading";

import "./styles.scss";

export const HISTORY_CARD_HEIGHT = 68;

export const HistoryInfiniteScroll: React.FC<HisotryInfiniteScrollProps> = ({
  header,
  headerSticky,
  filter: initialFilter,
  filterRef,
  firstPageOnly,
  chipsAlign = "left",
  className,
  noChips = false,
  setFilterRef,
  infiniteScrollStyle,
  handleOnScroll,
  memberUid,
}) => {
  const [height, setHeight] = useState<number | undefined>();
  const [filter, setFilter] = useState<HistoryFilter>({
    cards: initialFilter?.cards || new Set<string>(),
    currencies: initialFilter?.currencies || new Set<string>(),
    states: initialFilter?.states || new Set<RECORD_STATE>(),
    operationType: initialFilter?.operationType || [],
    limit: initialFilter?.limit || 1,
    to: initialFilter?.to,
    from: initialFilter?.from,
  });

  useEffect(() => {
    if (setFilterRef) {
      setFilterRef.current = (newFilters) => {
        setFilter({
          ...filter,
          ...newFilters,
        });
      };
    }
  }, [filter, setFilterRef]);

  useEffect(() => {
    if (filterRef) {
      filterRef.current = filter;
    }
  }, [filterRef, filter]);

  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [historyDetail, setHistoryDetail] = useState<
    UserHistoryResponse | undefined
  >();

  const {
    listContainerRef,
    data,
    setSize,
    size,
    forwardedRef,
    itemsTransitions,
    hasMore,
  } = useHistoryInfiniteScroll<HTMLDivElement>(
    HISTORY_CARD_HEIGHT,
    historyFiltersToParams(filter),
    firstPageOnly,
    memberUid,
  );

  useEffect(() => {
    const observeTarget = listContainerRef.current;
    const resizeObserver = new ResizeObserver((entries) => {
      // Assuming you want to track the dimensions of the first observed entry
      const { height } = entries[0].contentRect;

      if (height) {
        setHeight(height);
      }
    });

    if (observeTarget) {
      resizeObserver.observe(observeTarget);
    }

    // Cleanup function to unobserve when the component unmounts
    return () => {
      if (observeTarget) {
        resizeObserver.unobserve(observeTarget);
      }
    };
  }, [listContainerRef]);

  const selectedWithdraw =
    filter.operationType?.length === 1 &&
    filter.operationType?.[0] === HISTORY_OPERATION_TYPE.WITHDRAW;

  const selectedDeposit =
    filter.operationType?.length === 1 &&
    filter.operationType?.[0] === HISTORY_OPERATION_TYPE.DEPOSIT;

  return (
    <div
      className="HisotryInfiniteScroll fadeInSimple relative h-auto grow"
      ref={listContainerRef}
    >
      {height && (
        <InfiniteScroll
          dataLength={data.length} //This is important field to render the next data
          next={() => {
            setSize(size + 1);
          }}
          style={infiniteScrollStyle || { overflowX: "hidden" }}
          hasMore={firstPageOnly ? false : hasMore}
          loader={<MiniLoading />}
          height={height}
          className={classNames("list-none p-1", className)}
          endMessage={
            firstPageOnly ? (
              <div className="flex w-full justify-center">
                <Link to="/logged/history">
                  <Button fill="clear">
                    <FormattedMessage {...messages.showMoreButton} />
                  </Button>
                </Link>
              </div>
            ) : (
              <p
                style={{ textAlign: "center" }}
                className="text-xs font-semibold text-[--milkylb-color-secondary-100]"
              >
                Llegaste al final de la lista
              </p>
            )
          }
          onScroll={handleOnScroll}
        >
          {header}
          <div className="sticky -top-1 z-10 flex w-full flex-col">
            <div className="w-[calc(100% + 8px)] -ml-2 flex flex-row items-center justify-between border-b-4 border-l-8 border-r-8 border-t-8 border-white bg-white">
              {headerSticky && (
                <h3 className="mr-2 flex shrink-0 cursor-pointer items-center font-['Tomato_Grotesk'] text-2xl font-bold text-[--milkylb-color-neutral-600]">
                  {headerSticky}
                </h3>
              )}
              {!noChips && (
                <div
                  className={classNames(
                    "flex w-full",
                    chipsAlign === "right" ? "justify-end" : "justify-between",
                  )}
                >
                  <div className="flex flex-row gap-x-6">
                    <Pill
                      selected={selectedWithdraw}
                      onClick={() =>
                        setFilter({
                          ...filter,
                          operationType: selectedWithdraw
                            ? []
                            : [HISTORY_OPERATION_TYPE.WITHDRAW],
                        })
                      }
                    >
                      <FormattedMessage {...messages.withdraws} />
                    </Pill>
                    <Pill
                      onClick={() =>
                        setFilter({
                          ...filter,
                          operationType: selectedDeposit
                            ? []
                            : [HISTORY_OPERATION_TYPE.DEPOSIT],
                        })
                      }
                      selected={selectedDeposit}
                    >
                      <FormattedMessage {...messages.deposit} />
                    </Pill>
                  </div>
                  <AdjustIcon
                    onClick={() => setOpenFilter(true)}
                    className="history__header__icon ml-6 h-8 w-8 cursor-pointer rounded-full p-0.5"
                  />
                </div>
              )}
            </div>
            <div className="divider-gradient h-[15px] w-full" />
          </div>
          {itemsTransitions((style, item) => (
            <animated.div style={style}>
              <div ref={forwardedRef(item)} className="cursor-pointer pr-2">
                <CurrencyOperationCard
                  key={`${item.ticker_id}-${item.date}-${item.amount}-${item.tid}`}
                  operation={item}
                  onClick={() => setHistoryDetail(item)}
                />
              </div>
            </animated.div>
          ))}
        </InfiniteScroll>
      )}
      <Drawer
        width="100%"
        placement="right"
        keyboard={false}
        closable={false}
        onClose={() => setOpenFilter(false)}
        open={openFilter}
        getContainer=".ion-root"
      >
        <HistoryAdvancedSearch
          filter={filter}
          setFilter={setFilter}
          onClose={() => setOpenFilter(false)}
          memberUid={memberUid}
        />
      </Drawer>
      <HistoryDetails
        data={historyDetail}
        onClose={() => setHistoryDetail(undefined)}
      />
    </div>
  );
};
